import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { client } from '../lib/amplifyClient';
import { findBestUserRowByEmail, pickUserRecordForSession, type LoginUserRow } from '../lib/resolveAppUser';
import {
  ensureAppUserFromInvitation,
  ensureParentAppUser,
  resolveExistingAppUserId,
} from '../lib/ensureAppUser';
import { touchLastLoginForCognitoUser } from '../lib/touchLastLogin';
import { User, AuthState } from '../types/auth';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const checkUser = useCallback(async () => {
    // Avoid full-app loading flash on auth re-checks once a session is known.
    setState((prev) => {
      if (prev.isAuthenticated && prev.user) {
        return prev;
      }
      return { ...prev, isLoading: true };
    });
    try {
      const cognitoUser = await getCurrentUser();
      if (!cognitoUser) {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      const session = await fetchAuthSession();
      const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
      const emailRaw = (idPayload.email as string) || '';
      const email = emailRaw ? emailRaw.toLowerCase().trim() : '';

      let cognitoRow: LoginUserRow | null = null;
      try {
        const userResponse = await client.models.User.get({ id: cognitoUser.userId }, { authMode: 'apiKey' });
        if (userResponse.data) {
          cognitoRow = userResponse.data as LoginUserRow;
        }
      } catch {
        // fall through
      }

      const emailRow = email ? await findBestUserRowByEmail(client, email) : null;
      let userData = pickUserRecordForSession(cognitoRow, emailRow);

      if (!userData) {
        try {
          const userResponse = await client.models.User.get({ id: cognitoUser.userId }, { authMode: 'userPool' });
          if (userResponse.data) {
            userData = pickUserRecordForSession(userResponse.data as LoginUserRow, emailRow);
          }
        } catch {
          // ignore
        }
      }

      if (!userData && email) {
        try {
          const invitations = await client.models.UserInvitation.list({
            filter: { email: { eq: email } },
            authMode: 'apiKey',
          });
          const invitation =
            invitations.data?.find((inv) => inv.status !== 'REGISTERED') || invitations.data?.[0];
          if (invitation) {
            const idPayloadNames = idPayload as { given_name?: string; family_name?: string };
            const ensuredId = await ensureAppUserFromInvitation({
              cognitoUserId: cognitoUser.userId,
              email,
              firstName: idPayloadNames.given_name,
              lastName: idPayloadNames.family_name,
              invitation,
              passwordMarker: 'email_auth_user',
            });
            if (ensuredId) {
              const row = await client.models.User.get({ id: ensuredId }, { authMode: 'apiKey' });
              if (row.data) userData = row.data as LoginUserRow;
            }
          }
        } catch {
          // fall through
        }
      }

      if (!userData) {
        const resolvedId = await resolveExistingAppUserId(cognitoUser.userId, email);
        if (resolvedId) {
          const row = await client.models.User.get({ id: resolvedId }, { authMode: 'apiKey' });
          if (row.data) userData = row.data as LoginUserRow;
        }
      }

      // Parent self-signup: Cognito session exists but User row was never created / lost.
      // Only auto-create PARENT on parent onboarding paths (never invent therapist roles).
      if (!userData && email && typeof window !== 'undefined') {
        const path = window.location.pathname;
        const isParentOnboardingPath =
          path === '/kid-profile-form' ||
          path === '/parent-child-setup' ||
          path.startsWith('/intake-form') ||
          path.startsWith('/questionnaire');
        if (isParentOnboardingPath) {
          try {
            const idPayloadNames = idPayload as { given_name?: string; family_name?: string };
            const ensuredId = await ensureParentAppUser({
              cognitoUserId: cognitoUser.userId,
              email,
              firstName: idPayloadNames.given_name,
              lastName: idPayloadNames.family_name,
            });
            if (ensuredId) {
              const row = await client.models.User.get({ id: ensuredId }, { authMode: 'apiKey' });
              if (row.data) userData = row.data as LoginUserRow;
            }
          } catch {
            // fall through
          }
        }
      }

      if (userData) {
        setState({
          user: userData as User,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      }

      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    } catch {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    void checkUser();
    const remove = Hub.listen('auth', ({ payload }) => {
      const e = (payload as { event?: string })?.event;
      if (e === 'signIn' || e === 'signedIn' || e === 'signInWithRedirect') {
        void (async () => {
          try {
            const cognitoUser = await getCurrentUser();
            if (cognitoUser?.userId) {
              await touchLastLoginForCognitoUser(cognitoUser.userId);
            }
          } catch {
            /* non-blocking */
          }
          await checkUser();
        })();
        return;
      }
      if (e === 'signOut' || e === 'signedOut') {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    });
    return () => remove();
  }, [checkUser]);

  const login = async () => {
    // TODO: Implement login with Amplify
  };

  const logout = async () => {
    // TODO: Implement logout with Amplify
  };

  const updateUser = (user: User) => {
    setState((prev) => ({
      ...prev,
      user,
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
